<script setup>
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { modulesForCurrentUser } from "../db/auth.js";

defineProps({
  collapsed: { type: Boolean, default: false },
});

const router = useRouter();
const route = useRoute();
const allowedSet = ref(new Set());

const menuItems = [
  { code: "dashboard", label: "Dashboard", icon: "space_dashboard", path: "/dashboard/analytics" },
  { code: "inventory", label: "Inventario", icon: "inventory_2", path: "/inventario" },
  { code: "products", label: "Productos", icon: "category", path: "/productos" },
  { code: "menu", label: "Menú", icon: "restaurant_menu", path: "/menu" },
  { code: "sales", label: "Ventas", icon: "point_of_sale", path: "/ventas" },
  { code: "purchases", label: "Compras", icon: "shopping_bag", path: "/compras" },
  { code: "customers", label: "Clientes", icon: "group", path: "/clientes" },
  { code: "suppliers", label: "Proveedores", icon: "store", path: "/proveedores" },
  { code: "taxes", label: "Impuestos", icon: "request_quote", path: "/impuestos" },
  { code: "users", label: "Usuarios", icon: "manage_accounts", path: "/usuarios" },
];

onMounted(async () => {
  const allowed = await modulesForCurrentUser();
  allowedSet.value = new Set(allowed.map((m) => m.code));
});

async function navigate(item) {
  if (!allowedSet.value.has(item.code)) return;
  router.push(item.path);
}
</script>

<template>
  <aside class="sidebar" :class="{ collapsed }">
    <div class="logo-area">
      <div class="logo-icon">
        <span class="material-symbols-rounded">analytics</span>
      </div>
      <span v-if="!collapsed" class="logo-text">Tranquero</span>
    </div>

    <nav class="nav-menu">
      <a v-for="item in menuItems" :key="item.code" class="nav-item"
        :class="{ active: route.path === item.path, disabled: !allowedSet.has(item.code) }" @click="navigate(item)"
        :title="collapsed ? item.label : ''">
        <span class="material-symbols-rounded icon">{{ item.icon }}</span>
        <span v-if="!collapsed" class="label">{{ item.label }}</span>
        <span v-if="!collapsed && !allowedSet.has(item.code)" class="lock-icon">
          <span class="material-symbols-rounded">lock</span>
        </span>
      </a>
    </nav>

    <div v-if="!collapsed" class="sidebar-footer">
      <button class="logout-btn" @click="router.push('/')">
        <span class="material-symbols-rounded">arrow_back</span>
        <span>Volver</span>
      </button>
    </div>
  </aside>
</template>

<style scoped>
.sidebar {
  width: 260px;
  background: var(--surface);
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  height: 100vh;
  flex-shrink: 0;
  transition: width 0.3s ease;
}

.sidebar.collapsed {
  width: 80px;
}

.logo-area {
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  border-bottom: 1px solid var(--border);
  transition: padding 0.3s ease;
}

.sidebar.collapsed .logo-area {
  padding: 1.5rem 0.5rem;
  justify-content: center;
}

.logo-icon {
  width: 36px;
  height: 36px;
  background: var(--accent);
  border-radius: 8px;
  display: grid;
  place-items: center;
  color: white;
  flex-shrink: 0;
}

.logo-text {
  font-weight: 800;
  font-size: 1.2rem;
  letter-spacing: -0.5px;
  white-space: nowrap;
  overflow: hidden;
  opacity: 1;
  transition: opacity 0.2s ease;
}

.sidebar.collapsed .logo-text {
  opacity: 0;
  width: 0;
}

.nav-menu {
  flex: 1;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  overflow-y: auto;
  transition: padding 0.3s ease;
}

.sidebar.collapsed .nav-menu {
  padding: 1rem 0.5rem;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.8rem 1rem;
  border-radius: 12px;
  cursor: pointer;
  color: var(--muted);
  transition: all 0.2s;
  text-decoration: none;
  position: relative;
}

.sidebar.collapsed .nav-item {
  justify-content: center;
  padding: 0.8rem;
}

.nav-item:hover:not(.disabled) {
  background: rgba(0, 0, 0, 0.04);
  color: var(--text);
}

.nav-item.active {
  background: var(--accent);
  color: white;
  box-shadow: 0 4px 12px rgba(106, 92, 245, 0.3);
}

.nav-item.active .icon {
  color: white;
}

.nav-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.icon {
  font-size: 22px;
  flex-shrink: 0;
}

.label {
  font-weight: 600;
  font-size: 0.95rem;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  opacity: 1;
  transition: opacity 0.2s ease;
}

.sidebar.collapsed .label {
  opacity: 0;
  width: 0;
}

.lock-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.sidebar-footer {
  padding: 1rem;
  border-top: 1px solid var(--border);
  opacity: 1;
  transition: opacity 0.2s ease, padding 0.3s ease;
}

.sidebar.collapsed .sidebar-footer {
  opacity: 0;
  height: 0;
  padding: 0;
  overflow: hidden;
}

.logout-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.8rem;
  border: 1px solid var(--border);
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  color: var(--muted);
  transition: all 0.2s;
}

.logout-btn:hover {
  background: rgba(0, 0, 0, 0.04);
  color: var(--text);
}
</style>
